import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import { isEmptyContentfulEntry } from "@helpers/contentful";
import { getBaseUrl } from "@helpers/env";
import { isHelpArticleVisible } from "@helpers/helpsiteVisibility";
import usePreventImageDownload from "@hooks/usePreventImageDownload";
import { GoogleAnalytics, GoogleTagManager } from "@next/third-parties/google";
import type {
	IHelpsiteFields,
	IHelpsiteArticlesFields,
	IHelpsiteArticleFields,
	IHelpsiteArticles,
	IHelpsiteArticle,
	IPageSeoFields,
	IWebsiteFields,
	IModuleFooterFields,
	IModuleNavigationMenu,
	IAnnouncementModule,
} from "@types-contentful";
import type { Asset } from "contentful";
import ContentService from "@utils/content-service";
import { PreviewContentService } from "@utils/content-service";
import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Head from "next/head";
import React from "react";
import { useRouter } from "next/router";
import HelpMainPage from "@components/HelpSite/HelpMainPage";
import HelpTopic from "@components/HelpSite/HelpTopic";
import HelpArticle from "@components/HelpSite/HelpArticle";

// Helper type for entries with publishedVersion (not in generated types)
interface EntryWithPublishedVersion {
	sys: {
		publishedVersion?: number;
		[id: string]: unknown;
	};
	[field: string]: unknown;
}

/**
 * Next.js getStaticProps requires JSON-serializable data.
 * Contentful linked entries can contain cycles (for example relatedArticles -> relatedArticles).
 */
const toSerializable = <T,>(value: T): T => {
	const cloneWithoutCycles = (
		currentValue: unknown,
		ancestors: WeakSet<object>,
	): unknown => {
		if (!currentValue || typeof currentValue !== "object") {
			return currentValue;
		}

		const currentObject = currentValue as Record<string, unknown>;
		if (ancestors.has(currentObject)) {
			const entryId = (currentObject.sys as { id?: string } | undefined)?.id;
			return entryId ? { sys: { id: entryId, type: "Link" } } : null;
		}

		ancestors.add(currentObject);

		if (Array.isArray(currentValue)) {
			const result = currentValue.map((item) =>
				cloneWithoutCycles(item, ancestors),
			);
			ancestors.delete(currentObject);
			return result;
		}

		const result: Record<string, unknown> = {};
		for (const [key, nestedValue] of Object.entries(currentObject)) {
			const clonedValue = cloneWithoutCycles(nestedValue, ancestors);
			if (clonedValue !== undefined) {
				result[key] = clonedValue;
			}
		}

		ancestors.delete(currentObject);
		return result;
	};

	return JSON.parse(
		JSON.stringify(cloneWithoutCycles(value, new WeakSet<object>())),
	) as T;
};

const toContentTypeLink = (id: string) => ({
	sys: {
		id,
		type: "Link",
		linkType: "ContentType",
	},
});

const toMinimalArticleEntry = (
	entry: unknown,
	preview = false,
): IHelpsiteArticle | undefined => {
	if (!entry || typeof entry !== "object") return undefined;

	const typedEntry = entry as {
		sys?: { id?: string; [key: string]: unknown };
		fields?: Partial<IHelpsiteArticleFields>;
	};

	if (
		!typedEntry.sys?.id ||
		!typedEntry.fields?.articleRelativeUrl ||
		!isHelpArticleVisible(typedEntry.fields, { preview })
	) {
		return undefined;
	}

	return {
		sys: {
			...typedEntry.sys,
			id: typedEntry.sys.id,
			contentType:
				(typedEntry.sys as { contentType?: unknown }).contentType ||
				toContentTypeLink("helpsiteArticle"),
		},
		fields: {
			name: typedEntry.fields.name,
			iconRef: typedEntry.fields.iconRef,
			articleRelativeUrl: typedEntry.fields.articleRelativeUrl,
			articleMainHeader:
				typedEntry.fields.articleMainHeader || typedEntry.fields.name || "",
			subHeading: typedEntry.fields.subHeading,
		},
	} as unknown as IHelpsiteArticle;
};

const toMinimalTopicEntry = (entry: unknown): IHelpsiteArticles | undefined => {
	if (!entry || typeof entry !== "object") return undefined;

	const typedEntry = entry as {
		sys?: { id?: string; [key: string]: unknown };
		fields?: Partial<IHelpsiteArticlesFields>;
	};

	if (!typedEntry.sys?.id || !typedEntry.fields?.topicsRelativeUrl) {
		return undefined;
	}

	return {
		sys: {
			...typedEntry.sys,
			id: typedEntry.sys.id,
			contentType:
				(typedEntry.sys as { contentType?: unknown }).contentType ||
				toContentTypeLink("helpsiteArticles"),
		},
		fields: {
			name: typedEntry.fields.name,
			title: typedEntry.fields.title || typedEntry.fields.name || "",
			iconRef: typedEntry.fields.iconRef,
			content: typedEntry.fields.content,
			topicsRelativeUrl: typedEntry.fields.topicsRelativeUrl,
		},
	} as unknown as IHelpsiteArticles;
};

const sanitizeMainPageFields = (
	fields: IHelpsiteFields,
	preview = false,
): IHelpsiteFields => ({
	...fields,
	topics: (fields.topics || [])
		.map((topic) => toMinimalTopicEntry(topic))
		.filter(Boolean) as IHelpsiteArticles[],
	helpsiteFaqLinks: (fields.helpsiteFaqLinks || [])
		.map((link) => toMinimalArticleEntry(link, preview))
		.filter(Boolean) as IHelpsiteArticle[],
});

const sanitizeTopicPageFields = (
	fields: IHelpsiteArticlesFields,
	preview = false,
): IHelpsiteArticlesFields => ({
	...fields,
	articles: (fields.articles || [])
		.map((article) => toMinimalArticleEntry(article, preview))
		.filter(Boolean) as IHelpsiteArticle[],
});

const sanitizeArticlePageFields = (
	fields: IHelpsiteArticleFields,
	preview = false,
): IHelpsiteArticleFields => ({
	...fields,
	relatedArticles: (fields.relatedArticles || [])
		.map((entry) => toMinimalArticleEntry(entry, preview))
		.filter(Boolean) as IHelpsiteArticle[],
});

interface Props {
	pageType: "main" | "topic" | "article";
	pageData: IHelpsiteFields | IHelpsiteArticlesFields | IHelpsiteArticleFields;
	redirectToHome?: boolean;
	removeChatBubble?: boolean;
	pageMeta?: IPageSeoFields | null;
	footer?: IModuleFooterFields | null;
	logo?: Asset;
	navigation?: IModuleNavigationMenu;
	notificationBar?: IAnnouncementModule;
	notificationBarToggle?: string;
	relativeUrl?: string;
	/** When pageType is "article", the parent topic for the breadcrumb */
	parentTopic?: { title: string; href: string } | null;
	/**
	 * Article entry id → parent topic URL segment (from topicsRelativeUrl), from all
	 * helpsite topics. Used for FAQ links when the topic is not on the main page list.
	 */
	faqArticleTopicSlugById?: Record<string, string>;
}

const HelpPage: NextPage<Props> = ({
	pageType,
	pageData,
	redirectToHome = false,
	pageMeta,
	footer,
	logo,
	navigation,
	notificationBar,
	notificationBarToggle,
	relativeUrl,
	parentTopic,
	faqArticleTopicSlugById = {},
}) => {
	const router = useRouter();
	const currentPath = router.asPath.split("?")[0];

	React.useEffect(() => {
		if (redirectToHome) {
			void router.replace("/");
		}
	}, [redirectToHome, router]);

	// Use live updates if in preview mode
	// Note: useContentfulLiveUpdates expects Entry types, but we're passing Fields
	// This is acceptable as the hook handles both cases
	// biome-ignore lint/suspicious/noExplicitAny: Contentful live preview accepts any entry-like structure
	const pageWithUpdates = useContentfulLiveUpdates(pageData as any);
	const updatedPage = pageWithUpdates;

	const {
		title,
		description,
		ogtitle,
		ogdescription,
		ogimageMedia,
		keywords,
		author,
		ogurl,
		oglocale,
		ogimage,
		ogtype,
		noindex,
	} = pageMeta || {};

	const isNoIndex = !pageMeta || !!noindex;
	usePreventImageDownload();

	if (redirectToHome) {
		return null;
	}

	const renderPageContent = () => {
		switch (pageType) {
			case "main":
				return (
					<HelpMainPage
						pageData={updatedPage as IHelpsiteFields}
						footer={footer}
						faqArticleTopicSlugById={faqArticleTopicSlugById}
					/>
				);
			case "topic":
				return (
					<HelpTopic
						pageData={updatedPage as IHelpsiteArticlesFields}
						footer={footer}
					/>
				);
			case "article":
				return (
					<HelpArticle
						pageData={updatedPage as IHelpsiteArticleFields}
						footer={footer}
						parentTopic={parentTopic ?? undefined}
						articleTopicSlugById={faqArticleTopicSlugById}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<>
			<Head>
				<GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM_KEY || ""} />

				<title>{isEmptyContentfulEntry(title) ? "Help Center" : title}</title>
				<link rel="canonical" href={`${getBaseUrl()}${currentPath}`} />
				{!isEmptyContentfulEntry(author) && (
					<meta name="author" content={author} />
				)}
				{!isEmptyContentfulEntry(description) && (
					<meta name="description" content={description} />
				)}
				{!isEmptyContentfulEntry(keywords) && (
					<meta name="keywords" content={keywords} />
				)}

				{!isEmptyContentfulEntry(ogtitle) && (
					<meta property="og:title" content={ogtitle} />
				)}
				{!isEmptyContentfulEntry(ogdescription) && (
					<meta property="og:description" content={ogdescription} />
				)}
				{ogurl && !isEmptyContentfulEntry(ogurl) && (
					<meta property="og:url" content={ogurl} />
				)}
				{!isEmptyContentfulEntry(oglocale) && (
					<meta property="og:locale" content={oglocale} />
				)}
				{!isEmptyContentfulEntry(
					ogimageMedia?.fields?.file?.url || ogimage,
				) && (
					<meta
						property="og:image"
						content={ogimageMedia?.fields?.file?.url || ogimage}
					/>
				)}
				{!isEmptyContentfulEntry(ogtype) && (
					<meta property="og:type" content={ogtype} />
				)}

				{isNoIndex && <meta name="robots" content="noindex" />}
			</Head>

			{renderPageContent()}
		</>
	);
};

export default HelpPage;

export const getStaticPaths: GetStaticPaths = async () => {
	function removeSlash(param: string) {
		if (param.charAt(0) === "/") {
			return param.slice(1).trim();
		}
		return param;
	}

	/** For path generation, only URL fields are required */
	const helpSite = await ContentService.instance.getEntryById<IHelpsiteFields>(
		`${process.env.NEXT_PUBLIC_CONTENTFUL_HELPSITE_ID}`,
		1,
	);

	const paths: Array<{ params: { slug: string[] } }> = [];

	// Add main page
	paths.push({ params: { slug: [] } });

	// Fetch ALL topics and articles from Contentful (not just those linked from main page)
	// getEntriesByType returns Entry<T>[], where T is the Fields type
	const HELP_PATH_LINK_DEPTH = 1;

	const allTopics =
		(await ContentService.instance.getEntriesByType<IHelpsiteArticlesFields>(
			"helpsiteArticles",
			HELP_PATH_LINK_DEPTH,
		)) as unknown as IHelpsiteArticles[];
	const allArticles =
		(await ContentService.instance.getEntriesByType<IHelpsiteArticleFields>(
			"helpsiteArticle",
			0,
		)) as unknown as IHelpsiteArticle[];

	// Create a map of topic slugs for quick lookup
	const topicSlugMap = new Map<string, IHelpsiteArticles>();

	// Add topic pages (from main page AND standalone topics)
	const processedTopicIds = new Set<string>();

	// First, add topics from main page
	if (helpSite.fields.topics) {
		for (const topic of helpSite.fields.topics) {
			if (
				topic?.fields?.topicsRelativeUrl &&
				(topic as unknown as EntryWithPublishedVersion).sys.publishedVersion
			) {
				const topicSlug = removeSlash(topic.fields.topicsRelativeUrl);
				const topicSlugArray = topicSlug
					.split("/")
					.filter((segment) => segment !== "" && segment !== "help");

				paths.push({ params: { slug: topicSlugArray } });
				topicSlugMap.set(topicSlugArray.join("/"), topic);
				processedTopicIds.add(topic.sys.id);
			}
		}
	}

	// Add standalone topics (not in main page)
	for (const topic of allTopics) {
		const topicFields = topic.fields;
		if (
			topicFields?.topicsRelativeUrl &&
			(topic as unknown as EntryWithPublishedVersion).sys.publishedVersion &&
			!processedTopicIds.has(topic.sys.id)
		) {
			const topicSlug = removeSlash(topicFields.topicsRelativeUrl);
			const topicSlugArray = topicSlug
				.split("/")
				.filter((segment) => segment !== "" && segment !== "help");

			paths.push({ params: { slug: topicSlugArray } });
			topicSlugMap.set(topicSlugArray.join("/"), topic);
		}
	}

	// Add article pages
	const processedArticleIds = new Set<string>();

	// First, add articles from topics (both main page topics and standalone topics)
	for (const topic of allTopics) {
		const topicFields = topic.fields;
		if (
			topicFields?.articles &&
			(topic as unknown as EntryWithPublishedVersion).sys.publishedVersion
		) {
			const topicSlug = removeSlash(topicFields.topicsRelativeUrl || "");
			const topicSlugArray = topicSlug
				.split("/")
				.filter((segment) => segment !== "" && segment !== "help");

			for (const article of topicFields.articles || []) {
				if (
					article?.fields?.articleRelativeUrl &&
					(article as unknown as EntryWithPublishedVersion).sys
						.publishedVersion &&
					isHelpArticleVisible(article.fields)
				) {
					const articleSlug = removeSlash(article.fields.articleRelativeUrl);
					const articleSlugArray = articleSlug
						.split("/")
						.filter((segment) => segment !== "" && segment !== "help");

					// Construct full path: help/{topic-slug}/{article-slug}
					const fullArticleSlug = [
						...topicSlugArray,
						...articleSlugArray.filter((s) => !topicSlugArray.includes(s)),
					];

					paths.push({ params: { slug: fullArticleSlug } });
					processedArticleIds.add(article.sys.id);
				}
			}
		}
	}

	// Add standalone articles (not under any topic)
	// These would need a topic slug in their articleRelativeUrl or be at root level
	for (const article of allArticles) {
		const articleFields = article.fields;
		if (
			articleFields?.articleRelativeUrl &&
			(article as unknown as EntryWithPublishedVersion).sys.publishedVersion &&
			isHelpArticleVisible(articleFields) &&
			!processedArticleIds.has(article.sys.id)
		) {
			const articleSlug = removeSlash(articleFields.articleRelativeUrl);
			const articleSlugArray = articleSlug
				.split("/")
				.filter((segment) => segment !== "" && segment !== "help");

			// If articleRelativeUrl contains a topic-like path, use it
			// Otherwise, we'll need to determine the topic from the URL structure
			// For now, add it as-is (could be /help/{article-slug} or /help/{topic}/{article-slug})
			if (articleSlugArray.length > 0) {
				paths.push({ params: { slug: articleSlugArray } });
			}
		}
	}

	return {
		paths,
		fallback: "blocking",
	};
};

/** Strip circular references so the data can be serialised to JSON by Next.js.
 *  Uses an ancestor-stack approach so shared (non-circular) references are preserved. */
function removeCircularRefs<T>(obj: T): T {
	const ancestors: unknown[] = [];

	return JSON.parse(
		JSON.stringify(obj, function (_key, value) {
			if (typeof value !== "object" || value === null) {
				return value;
			}

			// `this` is the holder object at the current level.
			// Trim the ancestor stack back to the current holder's position.
			while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
				ancestors.pop();
			}

			if (ancestors.includes(value)) {
				return undefined; // circular — drop it
			}

			ancestors.push(value);
			return value;
		}),
	);
}

export const getStaticProps: GetStaticProps<
	Props,
	{ slug: Array<string> }
> = async (ctx) => {
	function removeSlash(param: string) {
		if (param.charAt(0) === "/") {
			return param.slice(1);
		}
		return param;
	}

	const { slug = [] } = ctx.params || {};
	const slugStr = slug.length ? slug.join("/") : "";

	if (slugStr.includes("undefined")) {
		return { notFound: true };
	}

	try {
		const client = ctx.preview ? PreviewContentService : ContentService;
		const HELP_MATCH_LINK_DEPTH = 1;
		const HELP_RENDER_LINK_DEPTH = 3;

		const helpSite = await client.instance.getEntryById<IHelpsiteFields>(
			`${process.env.NEXT_PUBLIC_CONTENTFUL_HELPSITE_ID}`,
			2,
		);

		// Fetch navigation data from Website entry. (same as main site)
		const websiteEntry = await client.instance.getEntryById<IWebsiteFields>(
			`${process.env.NEXT_PUBLIC_CONTENTFUL_WEBSITE_ID}`,
		);

		const { footer } = helpSite.fields;
		const { logo, navigation, notificationBar, notificationBarToggle } =
			websiteEntry.fields;
		const allTopics =
			(await client.instance.getEntriesByType<IHelpsiteArticlesFields>(
				"helpsiteArticles",
				HELP_MATCH_LINK_DEPTH,
			)) as unknown as IHelpsiteArticles[];

		// Determine relative URL for the current page
		let relativeUrl = "/help";
		if (slug.length === 1) {
			relativeUrl = `/help/${slug[0]}`;
		} else if (slug.length === 2) {
			relativeUrl = `/help/${slug[0]}/${slug[1]}`;
		}

		// Determine page type and find matching page
		let pageType: "main" | "topic" | "article" = "main";
		let pageData:
			| IHelpsiteFields
			| IHelpsiteArticlesFields
			| IHelpsiteArticleFields
			| null = null;
		let matchedTopicId: string | null = null;
		let matchedArticleId: string | null = null;
		let pageMeta: IPageSeoFields | undefined;
		let parentTopic: { title: string; href: string } | null = null;
		let removeChatBubble = false;
		let redirectToHome = false;
		const faqArticleTopicSlugById: Record<string, string> = {};

		// Build article entry id -> topic slug map (used by FAQ and related article links)
		for (const topic of allTopics) {
			if (
				!(topic as unknown as EntryWithPublishedVersion).sys.publishedVersion
			) {
				continue;
			}
			const topicUrl = topic.fields.topicsRelativeUrl;
			if (!topicUrl) continue;
			const topicSlug = removeSlash(topicUrl);
			for (const article of topic.fields.articles || []) {
				if (
					!article?.sys?.id ||
					!(article as unknown as EntryWithPublishedVersion).sys
						.publishedVersion ||
					!isHelpArticleVisible(article.fields, { preview: ctx.preview })
				) {
					continue;
				}
				if (faqArticleTopicSlugById[article.sys.id] !== undefined) continue;
				faqArticleTopicSlugById[article.sys.id] = topicSlug;
			}
		}

		// Check if it's the main page
		if (slugStr === "" || slugStr === "help") {
			pageData = sanitizeMainPageFields(helpSite.fields, ctx.preview);
			pageMeta = helpSite.fields.seo?.fields;
			pageType = "main";
			removeChatBubble = false;
		} else {
			// Fetch ALL topics and articles from Contentful (not just those linked from main page)
			// getEntriesByType returns Entry<T>[], where T is the Fields type
			const allArticles =
				(await client.instance.getEntriesByType<IHelpsiteArticleFields>(
					"helpsiteArticle",
					0,
				)) as unknown as IHelpsiteArticle[];

			// Check topics first (from main page AND standalone topics)
			const topicsToCheck = [
				...(helpSite.fields.topics || []),
				...allTopics.filter(
					(t) => !helpSite.fields.topics?.some((mt) => mt.sys.id === t.sys.id),
				),
			];

			for (const topic of topicsToCheck) {
				// Handle both Entry types and Fields types
				const topicFields =
					"fields" in topic ? topic.fields : (topic as IHelpsiteArticlesFields);

				if (
					!topicFields?.topicsRelativeUrl ||
					!(topic as unknown as EntryWithPublishedVersion).sys.publishedVersion
				) {
					continue;
				}

				// topicsRelativeUrl is like "/about-felix-plans", extract slug by removing leading slash
				const topicUrlSlug = removeSlash(topicFields.topicsRelativeUrl)
					.toLowerCase()
					.replace(/_/g, "-");

				// Check if it's a topic page (single segment match)
				if (slug.length === 1) {
					const incomingSlug = slug[0].toLowerCase().replace(/_/g, "-");
					if (incomingSlug === topicUrlSlug) {
						matchedTopicId = topic.sys.id;
						pageType = "topic";
						pageData = topicFields;
						removeChatBubble = Boolean(topicFields.removeChatBubble);
						break;
					}
				}

				// Check if it's an article in this topic (two segments)
				if (slug.length === 2 && topicFields.articles) {
					const topicSlugPart = slug[0].toLowerCase().replace(/_/g, "-");

					// First verify the topic matches
					if (topicSlugPart === topicUrlSlug) {
						for (const article of topicFields.articles) {
							if (
								!article?.fields?.articleRelativeUrl ||
								!(article as unknown as EntryWithPublishedVersion).sys
									.publishedVersion
							) {
								continue;
							}

							// articleRelativeUrl is like "/sim-type", extract slug by removing leading slash
							const articleUrlSlug = removeSlash(
								article.fields.articleRelativeUrl,
							)
								.toLowerCase()
								.replace(/_/g, "-");

							const articleSlugPart = slug[1].toLowerCase().replace(/_/g, "-");

							if (articleSlugPart === articleUrlSlug) {
								matchedArticleId = article.sys.id;
								pageType = "article";
								pageData = article.fields;
								removeChatBubble = Boolean(topicFields.removeChatBubble);
								parentTopic = {
									title: topicFields.title,
									href: `/help/${slug[0]}`,
								};
								break;
							}
						}
					}
				}

				if (pageData) break;
			}

			// If not found in topics, check standalone articles
			// These might have articleRelativeUrl that includes the full path
			if (!pageData) {
				for (const article of allArticles) {
					const articleFields = article.fields;
					if (
						!articleFields?.articleRelativeUrl ||
						!(article as unknown as EntryWithPublishedVersion).sys
							.publishedVersion
					) {
						continue;
					}

					const articleUrlSlug = removeSlash(articleFields.articleRelativeUrl)
						.toLowerCase()
						.replace(/_/g, "-");

					// Check if the slug matches the article's relative URL
					// Could be a single segment or multiple segments
					const articleSlugArray = articleUrlSlug
						.split("/")
						.filter((segment) => segment !== "" && segment !== "help");

					// Match if slug matches the article's URL structure
					if (
						slug.length === articleSlugArray.length &&
						slug.every(
							(segment, index) =>
								segment.toLowerCase().replace(/_/g, "-") ===
								articleSlugArray[index]?.toLowerCase().replace(/_/g, "-"),
						)
					) {
						matchedArticleId = article.sys.id;
						pageType = "article";
						pageData = articleFields;
						break;
					}
				}
			}
		}

		if (pageType === "topic" && matchedTopicId) {
			const topicEntry =
				await client.instance.getEntryById<IHelpsiteArticlesFields>(
					matchedTopicId,
					HELP_RENDER_LINK_DEPTH,
				);
			pageData = sanitizeTopicPageFields(topicEntry.fields, ctx.preview);
			pageMeta = topicEntry.fields.seo?.fields;
			removeChatBubble = Boolean(topicEntry.fields.removeChatBubble);
		}

		if (pageType === "article" && matchedArticleId) {
			const articleEntry =
				await client.instance.getEntryById<IHelpsiteArticleFields>(
					matchedArticleId,
					HELP_RENDER_LINK_DEPTH,
				);

			redirectToHome = !isHelpArticleVisible(articleEntry.fields, {
				preview: ctx.preview,
			});
			if (redirectToHome) {
				pageData = {
					articleRelativeUrl: articleEntry.fields.articleRelativeUrl,
					articleMainHeader: "",
					hideInProd: true,
				};
			} else {
				pageData = sanitizeArticlePageFields(articleEntry.fields, ctx.preview);
				pageMeta = articleEntry.fields.seo?.fields;
			}
		}

		if (!pageData) {
			return { notFound: true };
		}

		const props = toSerializable({
			pageType,
			pageData,
			redirectToHome,
			removeChatBubble,
			pageMeta: pageMeta || null,
			footer: footer?.fields || null,
			logo,
			navigation,
			notificationBar,
			notificationBarToggle,
			relativeUrl,
			parentTopic: parentTopic || null,
			faqArticleTopicSlugById,
		});

		return { props };
	} catch (error) {
		console.error("Error fetching help site data:", error);
		return { notFound: true };
	}
};
